import type { QueryFilter } from "@directus/sdk";
import { atom } from "jotai";
import { apiService } from "@/api";
import type { AppSchema } from "@/api/client";
import type { DeviceMessageRequest } from "@/api/model/device-message-request";
import {
  advanceCodecTimelineSources,
  type CodecEventTimelineRow,
  type CodecTimelineEntry,
  type CodecTimelineSourceKey,
  type CodecTimelineSourceProgressMap,
  codecEventsToTimelineEntries,
  createInitialCodecTimelineSourceProgress,
  hasAnyCodecTimelineSourceMore,
  type MeshDeviceEventTimelineRow,
  type MeshEventLabelMaps,
  type MessageRequestTimelineRow,
  mergeCodecTimelineSources,
  meshDeviceEventsToTimelineEntries,
  messageRequestsToTimelineEntries,
} from "@/pages/main/reports/codec-timeline/model";
import { dayjs } from "@/utils/date";
import { devicesAtom, projectsAtom, propertiesAtom, sectorsByProjectIdAtom } from "./property-domain";

/** Number of rows fetched from each source per page. */
export const CODEC_TIMELINE_PAGE_SIZE = 50;

/** User-facing filters for the CODEC timeline report. */
export interface CodecTimelineFilters {
  /** Selected LIC device id; null until the user (or the default) picks one. */
  codecId: string | null;
  /** Range start (ISO); events with start_time >= startDate are included. */
  startDate: string;
  /** Range end (ISO); events with start_time < endDate are included. */
  endDate: string;
}

/**
 * Pagination state: the page size plus the per-source offsets and exhaustion
 * flags. The fetch advances offsets as pages are loaded; a change of filters
 * resets every source to the first page.
 */
export interface CodecTimelinePagination {
  limit: number;
  sources: CodecTimelineSourceProgressMap;
}

const toIsoString = (value: Date | string | null | undefined) => {
  const date = dayjs(value ?? undefined);
  return date.isValid() ? date.toISOString() : dayjs().toISOString();
};

const normalizeTimelineRange = (start: Date | string, end: Date | string): { start: string; end: string } => {
  let startIso = toIsoString(start);
  let endIso = toIsoString(end);
  if (dayjs(startIso).isAfter(endIso)) {
    [startIso, endIso] = [endIso, startIso];
  }
  return { start: startIso, end: endIso };
};

const createDefaultCodecTimelineFilters = (): CodecTimelineFilters => {
  const end = dayjs();
  const start = end.clone().subtract(1, "day");
  const range = normalizeTimelineRange(start.toISOString(), end.toISOString());
  return {
    codecId: null,
    startDate: range.start,
    endDate: range.end,
  };
};

const isCodecTimelineSelectionReady = (filters: CodecTimelineFilters) => Boolean(filters.codecId);

const buildCodecTimelineIdentity = (filters: CodecTimelineFilters) =>
  `${filters.codecId ?? ""}|${filters.startDate}|${filters.endDate}`;

const codecTimelineFiltersBaseAtom = atom<CodecTimelineFilters>(createDefaultCodecTimelineFilters());
codecTimelineFiltersBaseAtom.debugLabel = "codecTimelineFiltersBaseAtom";

const codecTimelinePaginationBaseAtom = atom<CodecTimelinePagination>({
  limit: CODEC_TIMELINE_PAGE_SIZE,
  sources: createInitialCodecTimelineSourceProgress(),
});
codecTimelinePaginationBaseAtom.debugLabel = "codecTimelinePaginationBaseAtom";

export const codecTimelineEntriesAtom = atom<CodecTimelineEntry[]>([]);
codecTimelineEntriesAtom.debugLabel = "codecTimelineEntriesAtom";

export const codecTimelineLoadingAtom = atom<boolean>(false);
codecTimelineLoadingAtom.debugLabel = "codecTimelineLoadingAtom";

export const codecTimelineErrorAtom = atom<string | null>(null);
codecTimelineErrorAtom.debugLabel = "codecTimelineErrorAtom";

export const codecTimelineHasMoreAtom = atom<boolean>(false);
codecTimelineHasMoreAtom.debugLabel = "codecTimelineHasMoreAtom";

/**
 * Writable filters atom: any change that alters the report identity resets the
 * pagination and clears the previously loaded entries.
 */
export const codecTimelineFiltersAtom = atom(
  (get) => get(codecTimelineFiltersBaseAtom),
  (get, set, update: Partial<CodecTimelineFilters> | ((prev: CodecTimelineFilters) => CodecTimelineFilters)) => {
    const prev = get(codecTimelineFiltersBaseAtom);
    let next = typeof update === "function" ? update(prev) : { ...prev, ...update };
    const range = normalizeTimelineRange(next.startDate, next.endDate);
    next = { ...next, startDate: range.start, endDate: range.end };

    const changed = prev.codecId !== next.codecId || prev.startDate !== next.startDate || prev.endDate !== next.endDate;

    set(codecTimelineFiltersBaseAtom, next);

    if (changed) {
      set(codecTimelinePaginationBaseAtom, {
        limit: CODEC_TIMELINE_PAGE_SIZE,
        sources: createInitialCodecTimelineSourceProgress(),
      });
      set(codecTimelineEntriesAtom, []);
      set(codecTimelineHasMoreAtom, false);
      set(codecTimelineErrorAtom, null);
      set(codecTimelineLoadingAtom, false);
    }
  }
);
codecTimelineFiltersAtom.debugLabel = "codecTimelineFiltersAtom";

export const codecTimelineSelectionReadyAtom = atom((get) =>
  isCodecTimelineSelectionReady(get(codecTimelineFiltersBaseAtom))
);
codecTimelineSelectionReadyAtom.debugLabel = "codecTimelineSelectionReadyAtom";

export const codecTimelineIdentityAtom = atom((get) => buildCodecTimelineIdentity(get(codecTimelineFiltersBaseAtom)));
codecTimelineIdentityAtom.debugLabel = "codecTimelineIdentityAtom";

/**
 * Request key: the range identity plus the page size. Source offsets are
 * deliberately excluded — the fetch advances them internally, so including
 * them here would re-trigger the fetch effect after every page load.
 */
export const codecTimelineRequestKeyAtom = atom((get) => {
  const identity = get(codecTimelineIdentityAtom);
  const { limit } = get(codecTimelinePaginationBaseAtom);
  return `${identity}|${limit}`;
});
codecTimelineRequestKeyAtom.debugLabel = "codecTimelineRequestKeyAtom";

/**
 * Name maps for labeling mesh device events, derived from the loaded domain
 * data (devices, projects with sectors, and the property's water pumps).
 */
export const codecTimelineLabelMapsAtom = atom<MeshEventLabelMaps>((get) => {
  const devices = get(devicesAtom);
  const projects = get(projectsAtom);
  const properties = get(propertiesAtom);
  const sectorsByProjectId = get(sectorsByProjectIdAtom);

  const deviceIdentifierById = new Map<string, string>();
  for (const device of devices) {
    deviceIdentifierById.set(device.id, device.identifier);
  }

  const sectorNameById = new Map<string, string>();
  for (const project of projects) {
    for (const sector of sectorsByProjectId(project.id)) {
      if (sector.name) {
        sectorNameById.set(sector.id, sector.name);
      }
    }
  }

  const waterPumpNameById = new Map<string, string>();
  for (const property of properties) {
    for (const pump of property.water_pumps) {
      waterPumpNameById.set(pump.id, pump.label ?? pump.identifier);
    }
  }

  return { deviceIdentifierById, sectorNameById, waterPumpNameById };
});
codecTimelineLabelMapsAtom.debugLabel = "codecTimelineLabelMapsAtom";

/** One tagged page result from a source fetch. */
type TimelineSourceFetch = Promise<{
  rows: CodecEventTimelineRow[] | MeshDeviceEventTimelineRow[] | MessageRequestTimelineRow[];
  source: CodecTimelineSourceKey;
}>;

/**
 * Transforms each fetched page into timeline entries (per source) and returns
 * the merged, sorted entry list plus the per-source row counts used to advance
 * the pagination state.
 */
function buildFetchedSourceEntries(
  fetchedPages: Array<{
    rows: CodecEventTimelineRow[] | MeshDeviceEventTimelineRow[] | MessageRequestTimelineRow[];
    source: CodecTimelineSourceKey;
  }>,
  labelMaps: MeshEventLabelMaps
): { counts: Partial<Record<CodecTimelineSourceKey, number>>; entries: CodecTimelineEntry[] } {
  const counts = {} as Partial<Record<CodecTimelineSourceKey, number>>;
  const entryLists: CodecTimelineEntry[][] = [];

  for (const result of fetchedPages) {
    counts[result.source] = result.rows.length;
    if (result.source === "codecEvent") {
      entryLists.push(codecEventsToTimelineEntries(result.rows as CodecEventTimelineRow[]));
    } else if (result.source === "meshEvent") {
      entryLists.push(meshDeviceEventsToTimelineEntries(result.rows as MeshDeviceEventTimelineRow[], labelMaps));
    } else {
      entryLists.push(messageRequestsToTimelineEntries(result.rows as MessageRequestTimelineRow[]));
    }
  }

  return { counts, entries: mergeCodecTimelineSources(entryLists) };
}

/**
 * Builds the next-page request for each non-exhausted source at its own
 * offset. Exhausted sources are skipped entirely.
 */
function buildTimelineSourceRequests(options: {
  codecId: string;
  startDate: string;
  endDate: string;
  limit: number;
  sources: CodecTimelineSourceProgressMap;
}): TimelineSourceFetch[] {
  const { codecId, endDate, limit, sources, startDate } = options;
  const requests: TimelineSourceFetch[] = [];

  if (!sources.codecEvent.exhausted) {
    requests.push(
      apiService.codecEvent
        .readItemsByCodecId({
          id: codecId,
          start_date: startDate,
          end_date: endDate,
          limit,
          offset: sources.codecEvent.offset,
          sort_direction: "desc",
        })
        .then((rows) => ({ rows, source: "codecEvent" as const }))
    );
  }
  if (!sources.meshEvent.exhausted) {
    requests.push(
      apiService.codecMeshDeviceEvent
        .readItemsByCodecId({
          id: codecId,
          start_date: startDate,
          end_date: endDate,
          limit,
          offset: sources.meshEvent.offset,
          sort_direction: "desc",
        })
        .then((rows) => ({ rows, source: "meshEvent" as const }))
    );
  }
  if (!sources.messageRequest.exhausted) {
    requests.push(
      apiService.deviceMessageRequest
        .query({
          // The app schema types scheduled_at as a plain string (hiding the
          // date-range operators the Directus API supports on this datetime
          // column), so the typed filter needs a cast here.
          // SAFETY: the Directus API accepts _gte/_lt on scheduled_at; only
          // the SDK's generated filter type is narrower.
          filter: {
            device: { _eq: codecId },
            scheduled_at: { _gte: startDate, _lt: endDate },
          } as unknown as QueryFilter<AppSchema, DeviceMessageRequest>,
          fields: [
            "id",
            "payload_type",
            "status",
            "scheduled_at",
            "sent_at",
            "acknowledged_at",
            "attempts",
            "max_attempts",
            "last_error",
          ],
          sort: ["-scheduled_at"],
          limit,
          offset: sources.messageRequest.offset,
        })
        .then((rows) => ({ rows, source: "messageRequest" as const }))
    );
  }

  return requests;
}

/**
 * Fetches the next page of LIC-level and mesh device events for the selected
 * CODEC and range, transforming the raw rows into timeline entries, merging
 * them sorted newest first, and appending (deduplicated by entry key) when
 * paginating past the first page. Stale responses are discarded.
 */
export const fetchCodecTimelineAtom = atom(null, async (get, set) => {
  const filters = get(codecTimelineFiltersBaseAtom);
  const pagination = get(codecTimelinePaginationBaseAtom);
  const requestKey = get(codecTimelineRequestKeyAtom);
  const codecId = filters.codecId;

  if (!codecId) {
    set(codecTimelineEntriesAtom, []);
    set(codecTimelineHasMoreAtom, false);
    set(codecTimelineErrorAtom, null);
    set(codecTimelineLoadingAtom, false);
    return;
  }

  set(codecTimelineLoadingAtom, true);
  set(codecTimelineErrorAtom, null);

  try {
    const { limit, sources } = pagination;

    const fetchedPages = await Promise.all(
      buildTimelineSourceRequests({
        codecId,
        startDate: filters.startDate,
        endDate: filters.endDate,
        limit,
        sources,
      })
    );

    const latestKey = get(codecTimelineRequestKeyAtom);
    if (latestKey !== requestKey) {
      return;
    }

    const labelMaps = get(codecTimelineLabelMapsAtom);
    const { counts, entries: freshEntries } = buildFetchedSourceEntries(fetchedPages, labelMaps);
    const nextSources = advanceCodecTimelineSources(sources, counts, limit);
    const hasPrevious = get(codecTimelineEntriesAtom).length > 0;
    const previous = get(codecTimelineEntriesAtom);
    const seenKeys = hasPrevious ? new Set(previous.map((entry) => entry.key)) : new Set<string>();
    const merged = hasPrevious
      ? [...previous, ...freshEntries.filter((entry) => !seenKeys.has(entry.key))]
      : freshEntries;

    set(codecTimelinePaginationBaseAtom, { limit, sources: nextSources });
    set(codecTimelineEntriesAtom, merged);
    set(codecTimelineHasMoreAtom, hasAnyCodecTimelineSourceMore(nextSources));
  } catch (error) {
    const message = error instanceof Error ? error.message : "Erro ao carregar os eventos do CODEC.";
    set(codecTimelineErrorAtom, message);
    set(codecTimelineHasMoreAtom, false);
  } finally {
    const latestKey = get(codecTimelineRequestKeyAtom);
    if (latestKey === requestKey) {
      set(codecTimelineLoadingAtom, false);
      if (!get(codecTimelineEntriesAtom).length) {
        set(codecTimelineHasMoreAtom, false);
      }
    }
  }
});
fetchCodecTimelineAtom.debugLabel = "fetchCodecTimelineAtom";
